import { Component, EventEmitter, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CurrentUser } from "../../../common/providers/current-user";
import { EvaluationAthleteProvider } from '../../providers/evaluation-athlete-provider.services';
import { ArrayUtils } from "../../../common/providers/array-utils";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { FormControl } from '@angular/forms';
import { ConfigProvider } from "../../../config-provider.service";
import { Title } from '@angular/platform-browser';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { AppDatePipe } from '../../../common/pipes/app-date';
import { forkJoin, from, of, Subscription } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { Organization } from "../../../common/models/organization";
import { User } from "../../../common/models/user";
import { EvaluationAthlete } from "../../../common/models/evaluation-athlete";
import { Question } from "../../../common/models/question";
import { ApiBaseService } from "../../../common/providers/api-base-service";
import { OrganizationRole } from "../../../common/models/organization-role";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { EvaluationAthleteSideMenuComponent } from "../../components/side-menu/side-menu";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { NgClass, NgIf } from "@angular/common";
import { MatCardHeader, MatCardModule } from "@angular/material/card";
import { MatChip, MatChipsModule } from "@angular/material/chips";
import { EvaluationAthleteResultComponent } from "../../components/result/evaluation-athlete-result";
import { MainActionComponent } from "../../../common/components/main-action/main-action";
import { UsersProvider } from "../../../users/providers/users-provider";

@Component({
  selector: 'app-evaluations-athlete-show',
  templateUrl: './evaluations-athlete-show.component.html',
  styleUrls: ['./evaluations-athlete-show.scss'],
  imports: [
    PageContentComponent,
    HeaderComponent,
    SideMenuComponent,
    EvaluationAthleteSideMenuComponent,
    Error401Component,
    Error404Component,
    ModuleContentComponent,
    NgIf,
    MatCardModule,
    MatChipsModule,
    TranslateModule,
    NgClass,
    AppDatePipe,
    EvaluationAthleteResultComponent,
    MainActionComponent
  ],
  standalone: true
})
export class EvaluationsAthleteShowComponent implements OnInit, OnDestroy {

    public pageTitle = 'EvaluationAthleteList';
    public isLoading = true;
    public is401 = false;
    public is404 = false;

    public organization: Organization = {} as Organization;
    public athleteUser: User = {} as User;
    public evaluationAthlete: EvaluationAthlete | undefined;
    public questions: Question[] = [];

    public selectedEvaluations$ = new EventEmitter<Set<any>>();
    public selectedEvaluationsEvent$ = new EventEmitter<string>();
    public evaluationsAthletes: EvaluationAthlete[] = [];
    public evaluationsAthletesGrouped: EvaluationAthlete[] = [];
    public isSubmitting = false;

    public evaluationAthleteNotes: FormControl = new FormControl('');

    @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator | undefined;

    private subscriptions: Subscription[] = [];

    constructor(
        public conf: ConfigProvider,
        private title: Title,
        public breadcrumbProvider: BreadcrumbProvider,
        private currentUser: CurrentUser,
        private currentUserRoles: CurrentUserRolesProvider,
        private datePipe: AppDatePipe,
        private translater: TranslateService,
        private route: ActivatedRoute,
        private evaluationAthleteProvider: EvaluationAthleteProvider,
        private arrayUtils: ArrayUtils,
        private api: ApiBaseService,
        private usersProvider: UsersProvider,
    ) {
    }

    public ngOnInit() {
        // Load athlete and annual plan if it exists
        this.subscriptions.push(
            this.route.data.subscribe((data: any) => { // { organization: Organization; athlete: Athlete; evaluationAthlete: EvaluationAthlete }
                if (data.organization) {
                    this.organization = data.organization;
                }

                if (data.athlete) {
                    this.athleteUser = data.athlete;
                }

                if (data.evaluationAthlete) {
                    this.evaluationAthlete = data.evaluationAthlete;
                    this.evaluationAthleteNotes.setValue(this.evaluationAthlete?.notes);
                }

                this.loadData();
            })
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public loadData() {
      this.evaluationAthleteProvider.getAll(this.organization, this.athleteUser.athlete!).subscribe({
        next: (evaluationsAthletes) => {
          this.evaluationsAthletes = evaluationsAthletes;
          this.evaluationsAthletesGrouped = this.arrayUtils.groupByAttribute(evaluationsAthletes, 'evaluation', 'type');
          this.createPageTitle();
          this.createBreadcrumb();
          this.isLoading = false;
        }
      });
    }

    // public ROLE_COACH() {
    //     return OrganizationRole.ID_COACH;
    // }

    private createBreadcrumb() {

        const crumbs: Crumb[] = [];
        if (!this.currentUserRoles.have(OrganizationRole.ID_ATHLETE, this.organization.id!)){
            crumbs.push({
                shortTitle: 'Athletes',
                title: 'Athletes',
                url: '/' + this.organization!.code + '/athletes'
            });
        }
        crumbs.push({
            shortTitle: this.athleteUser.firstName?.substr(0, 1) + '. ' + this.athleteUser.lastName,
            title: this.athleteUser.firstName + ' ' + this.athleteUser.lastName,
            url: '/' + this.organization!.code + '/athletes/' + this.athleteUser.id
        });
        if (this.evaluationAthlete) {
            crumbs.push({
                shortTitle: this.translater.instant('Evaluation'),
                title: this.translater.instant('Evaluation'),
                url: '/' + this.organization!.code + '/athletes/' + this.athleteUser.id + '/evaluations/' + this.evaluationAthlete.id
            });
        } else {
            crumbs.push({
                shortTitle: this.translater.instant('EvaluationOfAthlete', { athlete: this.athleteUser.firstName + ' ' + this.athleteUser.lastName }),
                title: this.translater.instant('EvaluationOfAthlete', { athlete: this.athleteUser.firstName + ' ' + this.athleteUser.lastName }),
                url: '/' + this.organization!.code + '/athletes/' + this.athleteUser.id + '/evaluations'
            });
        }
        this.breadcrumbProvider.setDefaultCrumbs(crumbs);
    }

    private createPageTitle(){
        if (this.evaluationAthlete) {
            this.pageTitle = this.translater.instant('EvaluationOf', { date: this.datePipe.transform(this.evaluationAthlete.date, 'mediumDate') });
            this.title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
        } else {
            this.pageTitle = this.translater.instant('EvaluationOfAthlete', { athlete: this.athleteUser.firstName + ' ' + this.athleteUser.lastName });
            this.title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
        }
    }
}
